import * as THREE from "three/webgpu";
import {BehaviorSubject, type Observable} from 'rxjs';
import {createSlotAutoDimensions} from '../sketch/slot/createSlotAutoDimensions';
import {buildSlotCenterlinePoints} from '../sketch/slot/buildSlotCenterlinePoints';
import {SketchDrawingHandler, SketchDrawingMouseDownPayload} from './SketchDrawingHandler';
import type {SketchDrawingManager} from '../manager/SketchDrawingManager';
import {SketchDrawingTool} from './DrawingSharedTypes';

import {normalizeArcAngles} from '../tool/normalizeArcAngles';
import {buildArcFromThreePoints2D} from '../tool/buildArcFromThreePoints2D';

import {SketchPointObject} from '../mesh/sketch/SketchPointObject';
import {SketchPointKind} from '../mesh/sketch/ISketchObject';
import {SketchLineObject} from '../mesh/sketch/SketchLineObject';
import {type SketchArcDefinition, SketchArcObject} from '../mesh/sketch/SketchArcObject';
import {
    type ArcSlotUpdateParams,
    SketchSlotObject,
    type SlotArcEdgeDef,
    type SlotEdgeDecomposition,
    type StraightSlotUpdateParams,
} from '../mesh/sketch/SketchSlotObject';
import {PointsMaterialFactory} from '../material/PointsMaterialFactory';
import {LineMaterialFactory} from '../material/LineMaterialFactory';
import {syncThinChainLineGeometry, ThinChainLIneNodeMaterial} from '../material';
import {scheduleDisposableResourceDisposal} from '../utils';
import {ensureRaycastAccelerationForObjectTree, refitRaycastAccelerationForGeometry} from '../raycast-acceleration';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../sketch/config/SketchGeometryToleranceConfig';


/** 槽口预览颜色。 */
const SLOT_PREVIEW_COLOR = 0x53a9ff;
/** 槽口圆形与圆弧输入预览的固定采样段数。 */
const SLOT_CURVE_PREVIEW_SEGMENT_COUNT = 96;


/** 槽口细点画线预览透明度。 */
const SLOT_THIN_CHAIN_PREVIEW_OPACITY = 0.8;
/** 槽口细点画线预览线段长度。 */
const SLOT_THIN_CHAIN_PREVIEW_DASH_SIZE = 22;
/** 槽口细点画线预览间隔长度。 */
const SLOT_THIN_CHAIN_PREVIEW_GAP_SIZE = 4;
/** 槽口细点画线预览点段长度。 */
const SLOT_THIN_CHAIN_PREVIEW_DOT_SIZE = 4;



/**
 * 创建槽口第一段细点画线预览材质。
 * 说明：槽口第一段只是长度/弧端点输入引导，不是最终轮廓边，因此使用细点画线与正式构造中心线语义保持一致。
 * @param points 预览线点列。
 * @returns 槽口第一段细点画线材质。
 */
function createSlotFirstSegmentPreviewMaterial(points: THREE.Vector3[]): ThinChainLIneNodeMaterial {
    const material = new ThinChainLIneNodeMaterial({
        color: SLOT_PREVIEW_COLOR,
        linewidth: 1,
        dashSize: SLOT_THIN_CHAIN_PREVIEW_DASH_SIZE,
        gapSize: SLOT_THIN_CHAIN_PREVIEW_GAP_SIZE,
        dotSize: SLOT_THIN_CHAIN_PREVIEW_DOT_SIZE,
        opacity: SLOT_THIN_CHAIN_PREVIEW_OPACITY,
        transparent: true,
        points,
    });
    Reflect.set(material, 'name', '槽口第一段预览-屏幕空间细点画线材质');
    return material;
}



/**
 * 创建槽口轮廓预览中心线细点画线材质。
 * 说明：第二阶段中心线仍是临时构造引导，材质需要与第一段引导线保持一致，避免缩放后虚线节距和视觉语义分裂。
 * @param points 中心线点列。
 * @returns 槽口轮廓预览中心线细点画线材质。
 */
function createSlotCenterlinePreviewMaterial(points: THREE.Vector3[]): ThinChainLIneNodeMaterial {
    const material = new ThinChainLIneNodeMaterial({
        color: SLOT_PREVIEW_COLOR,
        linewidth: 1,
        dashSize: SLOT_THIN_CHAIN_PREVIEW_DASH_SIZE,
        gapSize: SLOT_THIN_CHAIN_PREVIEW_GAP_SIZE,
        dotSize: SLOT_THIN_CHAIN_PREVIEW_DOT_SIZE,
        opacity: SLOT_THIN_CHAIN_PREVIEW_OPACITY,
        transparent: true,
        points,
    });
    Reflect.set(material, 'name', '槽口轮廓预览-屏幕空间细点画线中心线材质');
    return material;
}


/**
 * 槽口绘制命令枚举。
 */
export enum SketchSlotCommand {
    /** 直槽口命令。 */
    Straight = 'sketch-slot',
    /** 中心点直槽口命令。 */
    CenterStraight = 'sketch-slot-center-straight',
    /** 三点圆弧槽口命令。 */
    ThreePointArc = 'sketch-slot-3-point-arc',
    /** 中心点圆弧槽口命令。 */
    CenterArc = 'sketch-slot-center-arc',
}

/**
 * 槽口长度类型枚举。
 */
export enum SlotLengthType {
    /** 中心到中心。 */
    CenterCenter = 'center-center',
    /** 总长度。 */
    Overall = 'overall',
}

/**
 * 判断命令是否为槽口绘制命令。
 * @param command 命令字符串。
 * @returns 是否为槽口绘制命令。
 */
export function isSketchSlotCommand(command: string): command is SketchSlotCommand {
    return Object.values(SketchSlotCommand).includes(command as SketchSlotCommand);
}

/**
 * 槽口绘制选项。
 */
export interface SlotOptions {
    /** 槽口绘制模式。 */
    mode: SketchSlotCommand;
    /** 是否添加尺寸标注。 */
    addDimensions: boolean;
    /** 是否为构造几何。 */
    forConstruction: boolean;
    /** 长度计算类型。 */
    lengthType: SlotLengthType;
}

/**
 * 槽口绘制参数。
 */
export interface SlotParameters {
    /** 槽口模式。 */
    mode?: SketchSlotCommand;
    /** 中心点。 */
    center?: [number, number, number];
    /** 中心点2D坐标。 */
    center2d?: [number, number];
    /** 起点。 */
    start?: [number, number, number];
    /** 起点2D坐标。 */
    start2d?: [number, number];
    /** 终点。 */
    end?: [number, number, number];
    /** 终点2D坐标。 */
    end2d?: [number, number];
    /** 宽度。 */
    width?: number;
    /** 中心长度。 */
    centerLength?: number;
    /** 总长度。 */
    overallLength?: number;
    /** 显示长度。 */
    displayLength?: number;
    /** 半径。 */
    radius?: number;
    /** 角度。 */
    angle?: number;
}

/**
 * 草图槽预览处理器。
 * 说明：
 * 1. 负责槽工具的预览与提交流程；
 * 2. 预览态创建的点、线、圆弧必须与正式草图对象隔离，禁止参与点捕捉。
 */
export class SketchSlotDrawingHandler extends SketchDrawingHandler {
    /** 当前处理器负责的工具类型。 */
    readonly tool: SketchDrawingTool = SketchDrawingTool.Slot;
    /** 槽工具选项。 */
    public slotOptions: SlotOptions = {
        mode: SketchSlotCommand.Straight,
        // 对齐 SOLIDWORKS：槽口「添加尺寸」默认勾选，绘制完成即带长度与宽度标注。
        addDimensions: true,
        forConstruction: false,
        lengthType: SlotLengthType.CenterCenter,
    };
    /** 槽参数。 */
    public slotParameters: SlotParameters | null = null;
    /** 槽参数实时流（绘制预览时发射当前参数，绘制结束时发射 null）。 */
    private readonly slotParametersSubject: BehaviorSubject<SlotParameters | null> = new BehaviorSubject<SlotParameters | null>(null);
    /** 预览用的槽口对象。 */
    private previewSlotObject: SketchSlotObject | null = null;

    /** 槽口第二阶段预览中心线对象缓存。 */
    private slotPreviewCenterline: THREE.Line | null = null;


    /** 槽口第一段细点画线预览对象缓存。 */
    private slotFirstSegmentPreviewLine: THREE.Line | null = null;
    /** 槽口第一段细点画线预览点列复用数组。 */
    private readonly slotFirstSegmentPreviewPoints = [new THREE.Vector3(), new THREE.Vector3()];

    /** 槽口圆形与圆弧输入预览对象缓存。 */
    private slotCurvePreviewLine: THREE.Line | null = null;
    /** 固定复用 97 个曲线采样点，避免鼠标移动期间持续创建数组与 Vector3。 */
    private readonly slotCurvePreviewPoints = Array.from({length: SLOT_CURVE_PREVIEW_SEGMENT_COUNT + 1}, () => new THREE.Vector3());
    /**
     * 构造函数。
     * @param sketchDrawingManager 草图绘制管理器实例。
     */
    constructor(sketchDrawingManager: SketchDrawingManager) {
        super(sketchDrawingManager);
    }

    /**
     * 观测槽口参数实时变化流。
     * @returns 可观察的槽口参数流，绘制中发射 SlotParameters，绘制结束发射 null。
     */
    public observeSlotParameters(): Observable<SlotParameters | null> {
        return this.slotParametersSubject.asObservable();
    }
    getRequiredPointCount(): number {
        if (this.slotOptions.mode === SketchSlotCommand.Straight || this.slotOptions.mode === SketchSlotCommand.CenterStraight) {
            return 3;
        }
        return 4;
    }
    get closable(): boolean {
        return false;
    }
    resetState(): void {
        this.drawingPoints = [];
        this.slotParameters = null;
        this.slotParametersSubject.next(null);

        this.clearSlotFirstSegmentPreviewCache();

        this.destroySlotPreview();
    }

    /**
     * 处理槽口绘制鼠标按下事件。
     * @param payload 鼠标按下载荷。
     */
    handleMouseDown(payload: SketchDrawingMouseDownPayload): void {
        const resolvedPoint = this.resolveSlotDrawingPoint(payload);
        if (!resolvedPoint) {
            console.log('无法获取槽口绘制位置');
            return;
        }
        this.drawingPoints.push(resolvedPoint.clone());
        if (this.drawingPoints.length !== this.getRequiredPointCount()) {
            return;
        }
        this.createSlot();
        this.cleanupDrawingState({
        });
    }
    /**
     * 获取槽工具绘制提示。
     * @returns 提示文本。
     */
    getDrawingHint(): string {
        const pointCount = this.drawingPoints.length;
        const mode = this.slotOptions.mode;
        let baseHint = '按步骤点击以定义槽口';

        if (mode === SketchSlotCommand.Straight) {
            if (pointCount === 0) {
                baseHint = '步骤 1/3：点击槽口起点';
            } else if (pointCount === 1) {
                baseHint = '步骤 2/3：点击槽口长度';
            } else {
                baseHint = '步骤 3/3：点击槽口宽度';
            }
        } else if (mode === SketchSlotCommand.CenterStraight) {
            if (pointCount === 0) {
                baseHint = '步骤 1/3：点击槽口中心';
            } else if (pointCount === 1) {
                baseHint = '步骤 2/3：点击槽口长度';
            } else {
                baseHint = '步骤 3/3：点击槽口宽度';
            }
        } else if (mode === SketchSlotCommand.ThreePointArc) {
            if (pointCount === 0) {
                baseHint = '步骤 1/4：点击圆弧起点';
            } else if (pointCount === 1) {
                baseHint = '步骤 2/4：点击圆弧终点';
            } else if (pointCount === 2) {
                baseHint = '步骤 3/4：点击第三点定义圆弧';
            } else {
                baseHint = '步骤 4/4：点击槽口宽度';
            }
        } else if (pointCount === 0) {
            baseHint = '步骤 1/4：点击圆弧中心';
        } else if (pointCount === 1) {
            baseHint = '步骤 2/4：点击半径点';
        } else if (pointCount === 2) {
            baseHint = '步骤 3/4：点击圆弧长度';
        } else {
            baseHint = '步骤 4/4：点击槽口宽度';
        }

        let extraInfo = '';
        if (this.slotParameters) {
            const width = this.slotParameters.width;
            if (typeof width === 'number') {
                extraInfo += ` - 槽宽: ${width.toFixed(2)}`;
            }
            if (mode === SketchSlotCommand.Straight || mode === SketchSlotCommand.CenterStraight) {
                const length = this.slotParameters.displayLength ?? this.slotParameters.centerLength;
                if (typeof length === 'number') {
                    extraInfo += `, 长度: ${length.toFixed(2)}`;
                }
            } else {
                const radius = this.slotParameters.radius;
                const angle = this.slotParameters.angle;
                if (typeof radius === 'number') {
                    extraInfo += `, 半径: ${radius.toFixed(2)}`;
                }
                if (typeof angle === 'number') {
                    extraInfo += `, 角度: ${angle.toFixed(1)}°`;
                }
            }
        }

        return `${baseHint}${extraInfo} | ESC取消`;
    }
    /**
     * 更新槽工具预览。
     * @param currentPoint 当前预览点。
     */
    updateSlotPreviewByHandler(currentPoint: THREE.Vector3): void {
        const firstPoint = this.drawingPoints[0];
        if (this.slotOptions.mode === SketchSlotCommand.Straight || this.slotOptions.mode === SketchSlotCommand.CenterStraight) {
            if (this.drawingPoints.length === 1) {

                this.createSlotFirstSegmentPreview(firstPoint, currentPoint);

                this.slotParameters = null;
                this.slotParametersSubject.next(null);
                return;
            }
            const previewPoints = [...this.drawingPoints, currentPoint];
            this.createSlotPreviewInHandler(previewPoints);
            return;
        }

        if (this.slotOptions.mode === SketchSlotCommand.ThreePointArc) {
            if (this.drawingPoints.length === 1) {

                this.createSlotFirstSegmentPreview(firstPoint, currentPoint);

                this.slotParameters = null;
                this.slotParametersSubject.next(null);
                return;
            }
            if (this.drawingPoints.length === 2) {
                this.createArcPreviewByThreePointsInHandler(firstPoint, this.drawingPoints[1], currentPoint);
                return;
            }
            const previewPoints = [...this.drawingPoints, currentPoint];
            this.createSlotPreviewInHandler(previewPoints);
            return;
        }

        if (this.drawingPoints.length === 1) {
            this.createCirclePreviewByCenterInHandler(firstPoint, currentPoint);
            this.slotParameters = null;
            this.slotParametersSubject.next(null);
            return;
        }
        if (this.drawingPoints.length === 2) {
            this.createArcPreviewByCenterInHandler(firstPoint, this.drawingPoints[1], currentPoint);
            return;
        }
        const previewPoints = [...this.drawingPoints, currentPoint];
        this.createSlotPreviewInHandler(previewPoints);
    }
    /**
     * 统一预览更新入口。
     * @param currentPoint 当前预览点。
     */
    updatePreview(currentPoint: THREE.Vector3): void {
        this.updateSlotPreviewByHandler(currentPoint);
    }

    /**
     * 创建槽口实体。
     */
    public createSlot(): void {
        // 从绘制点构建槽口数据。
        const slotData = this.buildSlotFromPoints(this.drawingPoints);
        if (!slotData) {
            return;
        }

        // 从绘制点计算边缘分解数据。
        const decomposition = this.computeSlotEdgeDecomposition(this.drawingPoints);
        if (!decomposition) {
            return;
        }

        // 组装 SketchSlotObject。
        const slotObject = this.assembleSketchSlotObject(decomposition, false);
        if (!slotObject) {
            return;
        }

        // 将几何元数据存储在槽口对象上（用于辅助可视化）。
        slotObject.geometryData = slotData.geometryData;

        // 添加到草图实体集合并通知创建事件。
        this.viewer.addSketchObject(slotObject);
        this.sketchDrawingManager.notifySketchEntityCreated(slotObject);

        this.applySlotAutoDimensions(slotObject);


        // 创建实体后同步上色轮廓。
        if (this.viewer.shadedSketchContoursEnabled) {
            slotObject.syncContour();
        }


        // 更新槽参数面板。
        this.slotParameters = slotData.parameters;
        this.slotParametersSubject.next(this.slotParameters);

        // 创建完成后程序化选中新槽口，使属性面板参数可继续直接编辑。
        this.viewer.entitySelectionManager?.selectEntityProgrammatically(slotObject);
    }
    /**
     * 清空槽口第一段细点画线预览缓存引用。
     */
    private clearSlotFirstSegmentPreviewCache(): void {
        this.slotFirstSegmentPreviewLine = null;
    }
    /** 清空槽口曲线输入预览缓存引用。 */
    private clearSlotCurvePreviewCache(): void {
        this.slotCurvePreviewLine = null;
    }

    /**
     * 清空槽口第二阶段中心线预览缓存引用。
     */
    private clearSlotPreviewCenterlineCache(): void {
        this.slotPreviewCenterline = null;
    }


    /**
     * 判断槽口第一段细点画线预览是否仍可复用。
     * @returns 当前缓存对象是否仍由绘制管理器持有。
     */
    private isSlotFirstSegmentPreviewActive(): boolean {
        return Boolean(
            this.slotFirstSegmentPreviewLine
            && this.sketchDrawingManager.previewLine === this.slotFirstSegmentPreviewLine,
        );
    }

    /**
     * 创建槽口第一段细点画线预览。
     * 说明：
     * 1. 直槽口第一段表示长度方向，三点圆弧槽口第一段表示圆弧起终点弦线；
     * 2. 这条线只是绘制引导，不是最终槽口轮廓，因此必须使用细点画线材质；
     * 3. 进入后续槽口轮廓预览时由 `createSlotPreviewInHandler()` 统一清理。
     * @param start 起点世界坐标。
     * @param end 当前预览终点世界坐标。
     */
    private createSlotFirstSegmentPreview(start: THREE.Vector3, end: THREE.Vector3): void {
        const points = this.updateSlotFirstSegmentPreviewPoints(start, end);
        if (!this.isSlotFirstSegmentPreviewActive()) {
            this.destroyPreview();
            const geometry = new THREE.BufferGeometry().setFromPoints(points);
            const material = createSlotFirstSegmentPreviewMaterial(points);
            this.sketchDrawingManager.previewLine = new THREE.Line(geometry, material);
            this.slotFirstSegmentPreviewLine = this.sketchDrawingManager.previewLine;
            syncThinChainLineGeometry(this.sketchDrawingManager.previewLine);
            this.viewer.addObject(this.sketchDrawingManager.previewLine);
            return;
        }

        this.updateSlotThinChainPreviewLineGeometry(this.slotFirstSegmentPreviewLine as THREE.Line, points);
    }

    /**
     * 原地更新槽口第一段预览点列。
     * @param start 起点世界坐标。
     * @param end 当前预览终点世界坐标。
     * @returns 可写入预览几何的复用点列。
     */
    private updateSlotFirstSegmentPreviewPoints(
        start: THREE.Vector3,
        end: THREE.Vector3,
    ): THREE.Vector3[] {
        this.slotFirstSegmentPreviewPoints[0].copy(start);
        this.slotFirstSegmentPreviewPoints[1].copy(end);
        return this.slotFirstSegmentPreviewPoints;
    }

    /**
     * 同步槽口线对象点列与射线加速结构；同拓扑原位改写顶点缓冲，
     * 仅在点数变化时替换几何体并延迟释放旧资源。
     * @param line 待更新的槽口线对象。
     * @param points 新点列。
     */
    private updateSlotLineGeometryPoints(line: THREE.Line, points: THREE.Vector3[]): void {
        const positionAttribute = line.geometry.getAttribute('position');
        let replacedGeometry = false;
        if (positionAttribute instanceof THREE.BufferAttribute && positionAttribute.count === points.length) {
            points.forEach((point, index) => {
                positionAttribute.setXYZ(index, point.x, point.y, point.z);
            });
            positionAttribute.needsUpdate = true;
        } else {
            // 拓扑变化时创建新几何体，旧几何体延迟到安全帧释放。
            const previousGeometry = line.geometry;
            line.geometry = new THREE.BufferGeometry().setFromPoints(points);
            scheduleDisposableResourceDisposal(previousGeometry);
            replacedGeometry = true;
        }
        line.geometry.computeBoundingBox();
        line.geometry.computeBoundingSphere();
        if (replacedGeometry) {
            ensureRaycastAccelerationForObjectTree(line);
        } else {
            refitRaycastAccelerationForGeometry(line.geometry);
        }
    }
    /**
     * 原地更新槽口细点画线预览几何。
     * @param line 槽口细点画线预览线。
     * @param points 新点列。
     */
    private updateSlotThinChainPreviewLineGeometry(line: THREE.Line, points: THREE.Vector3[]): void {
        this.updateSlotLineGeometryPoints(line, points);
        syncThinChainLineGeometry(line);
    }

    /**
     * 获取槽口绘制所需的点数。
     * @param mode 槽口模式。
     * @returns 所需点数。
     */
    public getSlotPointCount(mode: SlotOptions['mode']): number {
        if (mode === SketchSlotCommand.Straight || mode === SketchSlotCommand.CenterStraight) {
            return 3;
        }
        return 4;
    }

    /**
     * 销毁当前绘制预览并清空槽口第一段预览缓存。
     * 说明：槽口第一段预览复用对象；一旦进入后续阶段或清理绘制状态，缓存必须同步失效。
     */
    protected override destroyPreview(): void {
        super.destroyPreview();
        this.clearSlotFirstSegmentPreviewCache();
        this.clearSlotCurvePreviewCache();

        this.clearSlotPreviewCenterlineCache();

    }

    public isArcSlotMode(mode: SlotOptions['mode']): boolean {
        return mode === SketchSlotCommand.ThreePointArc || mode === SketchSlotCommand.CenterArc;
    }

    public resolveSlotLengthType(mode: SlotOptions['mode'], lengthType: SlotLengthType): SlotLengthType {
        if (this.isArcSlotMode(mode)) {
            return SlotLengthType.CenterCenter;
        }
        return lengthType;
    }

    /**
     * 按「添加尺寸」选项为新建槽口自动标注（对齐 SOLIDWORKS Add Dimensions）。
     *
     * 说明：
     * 1. 生成的是真实尺寸（进仓库、可驱动、可持久化），不是显示层装饰；
     * 2. 补丁暂存到基类的待提交队列，由绘制事务与槽口几何**同一笔**提交，
     *    撤销一步即可回到"没有槽口也没有尺寸"。
     *
     * @param slot 新建槽口。
     */
    private applySlotAutoDimensions(slot: SketchSlotObject): void {
        if (!this.slotOptions.addDimensions) {
            return;
        }

        const sketchGroup = this.viewer.sketchGroupManager?.activeSketchGroup ?? null;
        const repository = this.viewer.sketchGroupManager?.dimensionRepository ?? null;

        if (!sketchGroup || !repository) {
            return;
        }

        const patches = createSlotAutoDimensions({
            repository,
            sketchId: sketchGroup.persistentId,
            slot,
            lengthType: this.slotOptions.lengthType,
        });

        if (patches.length === 0) {
            return;
        }

        this.pendingHistoryPatches.push(...patches);
    }


    /**
     * 将几何数据中的三元组转换为世界坐标向量。
     * @param value 几何数据字段值。
     * @returns 解析成功的世界坐标；失败返回 null。
     */
    private createVector3FromGeometryTuple(value: unknown): THREE.Vector3 | null {
        const tuple = this.parseVector3Tuple(value);
        if (!tuple) {
            return null;
        }
        return new THREE.Vector3(tuple[0], tuple[1], tuple[2]);
    }

    /**
     * 构建直槽口预览对象的原地更新参数。
     * @param decomposition 当前槽口边分解数据。
     * @param geometryData 当前槽口几何元数据。
     * @returns 直槽口更新参数；数据无效时返回 null。
     */
    private createStraightSlotPreviewUpdateParams(
        decomposition: SlotEdgeDecomposition,
        geometryData: Record<string, any>,
    ): StraightSlotUpdateParams | null {
        if (decomposition.type !== 'straight') {
            return null;
        }

        const startCenterWorld = this.createVector3FromGeometryTuple(geometryData.centerlineStart);
        const endCenterWorld = this.createVector3FromGeometryTuple(geometryData.centerlineEnd);
        const centerWorld = this.createVector3FromGeometryTuple(geometryData.center);
        const width = Number(geometryData.width);
        const angle = Number(geometryData.angle);
        if (
            !startCenterWorld
            || !endCenterWorld
            || !centerWorld
            || !Number.isFinite(width)
            || width <= 0
            || !Number.isFinite(angle)
        ) {
            return null;
        }

        return {
            p1World: decomposition.p1World,
            p2World: decomposition.p2World,
            p3World: decomposition.p3World,
            p4World: decomposition.p4World,
            startCenterWorld,
            endCenterWorld,
            centerWorld,
            halfWidth: width / 2,
            theta: THREE.MathUtils.degToRad(angle),
        };
    }

    /**
     * 计算圆弧槽口中心线弧中点世界坐标。
     * @param centerWorld 圆弧中心世界坐标。
     * @param radius 中心线半径。
     * @param startAngle 起始角。
     * @param endAngle 终止角。
     * @param clockwise 是否顺时针。
     * @returns 中心线弧中点世界坐标。
     */
    private createArcSlotCenterlineMidpointWorld(
        centerWorld: THREE.Vector3,
        radius: number,
        startAngle: number,
        endAngle: number,
        clockwise: boolean,
    ): THREE.Vector3 {
        const normalized = normalizeArcAngles(startAngle, endAngle, clockwise);
        const midpointAngle = clockwise
            ? normalized.start - normalized.angleRange / 2
            : normalized.start + normalized.angleRange / 2;
        const centerLocal = this.referencePlane.worldToLocal(centerWorld);
        const midpointLocal = new THREE.Vector2(
            centerLocal.x + radius * Math.cos(midpointAngle),
            centerLocal.y + radius * Math.sin(midpointAngle),
        );
        return this.referencePlane.localToWorld(midpointLocal);
    }

    /**
     * 构建圆弧槽口预览对象的原地更新参数。
     * @param decomposition 当前槽口边分解数据。
     * @param geometryData 当前槽口几何元数据。
     * @returns 圆弧槽口更新参数；数据无效时返回 null。
     */
    private createArcSlotPreviewUpdateParams(
        decomposition: SlotEdgeDecomposition,
        geometryData: Record<string, any>,
    ): ArcSlotUpdateParams | null {
        if (decomposition.type !== 'arc') {
            return null;
        }

        const centerWorld = this.createVector3FromGeometryTuple(geometryData.center);
        const radius = Number(geometryData.radius);
        const width = Number(geometryData.width);
        const startAngle = Number(geometryData.startAngle);
        const endAngle = Number(geometryData.endAngle);
        const clockwise = Boolean(geometryData.clockwise);
        if (
            !centerWorld
            || !Number.isFinite(radius)
            || radius <= 0
            || !Number.isFinite(width)
            || width <= 0
            || !Number.isFinite(startAngle)
            || !Number.isFinite(endAngle)
        ) {
            return null;
        }

        return {
            centerWorld,
            radius,
            halfWidth: width / 2,
            startAngle,
            endAngle,
            clockwise,
            p1World: decomposition.p1World,
            p2World: decomposition.p2World,
            p3World: decomposition.p3World,
            p4World: decomposition.p4World,
            startCapCenterWorld: decomposition.startCapArc.centerWorld,
            endCapCenterWorld: decomposition.endCapArc.centerWorld,
            centerArcMidpointWorld: this.createArcSlotCenterlineMidpointWorld(
                centerWorld,
                radius,
                startAngle,
                endAngle,
                clockwise,
            ),
        };
    }

    /**
     * 判断现有槽口预览对象是否可复用。
     * 说明：模式、构造线状态或拓扑类型变化时必须重建，因为这些状态会影响子对象材质和对象结构。
     * @param previewSlot 现有预览槽口对象。
     * @param decomposition 当前边分解数据。
     * @returns 可复用返回 true。
     */
    private canReuseSlotPreviewObject(
        previewSlot: SketchSlotObject,
        decomposition: SlotEdgeDecomposition,
    ): boolean {
        return previewSlot.mode === this.slotOptions.mode
            && previewSlot.isConstruction === this.slotOptions.forConstruction
            && previewSlot.isArcSlot() === (decomposition.type === 'arc');
    }

    /**
     * 原地更新现有槽口预览对象。
     * @param decomposition 当前槽口边分解数据。
     * @param geometryData 当前槽口几何元数据。
     * @returns 更新成功返回 true；不可复用或参数无效时返回 false。
     */
    private updateExistingSlotPreviewObject(
        decomposition: SlotEdgeDecomposition,
        geometryData: Record<string, any>,
    ): boolean {
        const previewSlot = this.previewSlotObject;
        if (!previewSlot || !this.canReuseSlotPreviewObject(previewSlot, decomposition)) {
            return false;
        }

        if (decomposition.type === 'straight') {
            const params = this.createStraightSlotPreviewUpdateParams(decomposition, geometryData);
            if (!params) {
                return false;
            }
            previewSlot.updateStraightSlotGeometry(params);
        } else {
            const params = this.createArcSlotPreviewUpdateParams(decomposition, geometryData);
            if (!params) {
                return false;
            }
            previewSlot.updateArcSlotGeometry(params);
        }

        previewSlot.geometryData = geometryData;
        return true;
    }

    /**
     * 移除并释放槽口第二阶段中心线预览。
     */
    private destroySlotPreviewCenterline(): void {
        const centerline = this.slotPreviewCenterline;
        if (!centerline) {
            return;
        }

        if (centerline.parent) {
            centerline.parent.remove(centerline);
        }
        centerline.geometry?.dispose();
        const material = centerline.material;
        if (Array.isArray(material)) {
            material.forEach((item) => item.dispose());
        } else {
            material?.dispose();
        }
        this.sketchDrawingManager.previewConstructionLines = this.sketchDrawingManager.previewConstructionLines
            .filter((line) => line !== centerline);
        this.clearSlotPreviewCenterlineCache();
    }

    /**
     * 创建或更新槽口第二阶段中心线预览。
     * @param geometryData 当前槽口几何元数据。
     */
    private syncSlotPreviewCenterline(geometryData: Record<string, any>): void {
        const centerlinePoints = this.buildSlotCenterlinePoints(geometryData);
        if (!centerlinePoints || centerlinePoints.length < 2) {
            this.destroySlotPreviewCenterline();
            return;
        }

        if (
            !this.slotPreviewCenterline
            || !this.sketchDrawingManager.previewConstructionLines.includes(this.slotPreviewCenterline)
        ) {
            const centerlineGeometry = new THREE.BufferGeometry().setFromPoints(centerlinePoints);
            const centerlineMaterial = createSlotCenterlinePreviewMaterial(centerlinePoints);
            const centerline = new THREE.Line(centerlineGeometry, centerlineMaterial);
            syncThinChainLineGeometry(centerline);
            this.slotPreviewCenterline = centerline;
            this.sketchDrawingManager.previewConstructionLines.push(centerline);
            this.viewer.addObject(centerline);
            return;
        }

        this.updateSlotThinChainPreviewLineGeometry(this.slotPreviewCenterline, centerlinePoints);
    }

    /**
     * 清理无效槽口预览输入产生的旧预览状态。
     */
    private clearInvalidSlotPreviewState(): void {
        this.destroyPreview();
        this.destroySlotPreview();
        this.slotParameters = null;
        this.slotParametersSubject.next(null);
    }


    /** 在处理器内创建槽口轮廓预览。 */
    private createSlotPreviewInHandler(points: THREE.Vector3[]): void {
        const slotData = this.buildSlotFromPoints(points);
        if (!slotData) {
            this.clearInvalidSlotPreviewState();
            return;
        }

        // 计算边缘分解数据。
        const decomposition = this.computeSlotEdgeDecomposition(points);
        if (!decomposition) {
            this.clearInvalidSlotPreviewState();
            return;
        }

        const hasUpdatedExistingPreview = this.updateExistingSlotPreviewObject(decomposition, slotData.geometryData);
        if (!hasUpdatedExistingPreview) {
            this.destroyPreview();
            this.destroySlotPreview();

            // 组装预览用 SketchSlotObject。
            const previewSlot = this.assembleSketchSlotObject(decomposition, true);
            if (!previewSlot) {
                this.slotParameters = null;
                this.slotParametersSubject.next(null);
                return;
            }

            previewSlot.geometryData = slotData.geometryData;
            this.previewSlotObject = previewSlot;
            this.viewer.addObject(this.previewSlotObject);
        }

        this.syncSlotPreviewCenterline(slotData.geometryData);
        this.slotParameters = slotData.parameters;
        this.slotParametersSubject.next(this.slotParameters);
    }

    public buildSlotCenterlinePoints(geometryData: Record<string, any>): THREE.Vector3[] | null {
        // 采样实现全仓唯一一份（尺寸驱动/撤销恢复后的刷新与这里同源），处理器只补预览态的回退模式。
        return buildSlotCenterlinePoints(geometryData, this.slotOptions?.mode ?? SketchSlotCommand.Straight);
    }
    public buildStraightSlotOutline2D(
        startCenter: THREE.Vector2,
        endCenter: THREE.Vector2,
        halfWidth: number,
        capSegments = 24
    ): THREE.Vector2[] {
        const axis = endCenter.clone().sub(startCenter);
        if (
            axis.lengthSq() < SKETCH_GEOMETRY_TOLERANCE_CONFIG.slotAxisLengthSquared
            || !Number.isFinite(halfWidth)
            || halfWidth <= 0
        ) {
            return [];
        }

        const theta = Math.atan2(axis.y, axis.x);
        const normalAngle = theta + Math.PI / 2;
        const pointOnCap = (center: THREE.Vector2, angle: number): THREE.Vector2 =>
            new THREE.Vector2(
                center.x + Math.cos(angle) * halfWidth,
                center.y + Math.sin(angle) * halfWidth
            );

        const points: THREE.Vector2[] = [];
        points.push(pointOnCap(startCenter, normalAngle));
        points.push(pointOnCap(endCenter, normalAngle));

        for (let i = 1; i <= capSegments; i += 1) {
            const angle = normalAngle - (i / capSegments) * Math.PI;
            points.push(pointOnCap(endCenter, angle));
        }

        points.push(pointOnCap(startCenter, normalAngle - Math.PI));

        for (let i = 1; i <= capSegments; i += 1) {
            const angle = normalAngle - Math.PI - (i / capSegments) * Math.PI;
            points.push(pointOnCap(startCenter, angle));
        }

        if (points.length > 0) {
            points.push(points[0].clone());
        }
        return points;
    }

    public buildArcSlotOutline2D(
        center: THREE.Vector2,
        radius: number,
        startAngle: number,
        endAngle: number,
        clockwise: boolean,
        halfWidth: number,
        arcSegments = 64,
        capSegments = 24
    ): THREE.Vector2[] | null {
        if (!Number.isFinite(radius) || radius <= 0 || !Number.isFinite(halfWidth) || halfWidth <= 0) {
            return null;
        }

        const outerRadius = radius + halfWidth;
        const innerRadius = Math.max(radius - halfWidth, SKETCH_GEOMETRY_TOLERANCE_CONFIG.slotMinimumRadius);
        const sampleAngles = (from: number, to: number, cw: boolean, segments: number): number[] => {
            const twoPi = Math.PI * 2;
            const start = from;
            let end = to;
            if (cw) {
                while (end > start) {
                    end -= twoPi;
                }
            } else {
                while (end < start) {
                    end += twoPi;
                }
            }
            const step = (end - start) / Math.max(segments, 1);
            const values: number[] = [];
            for (let i = 0; i <= segments; i += 1) {
                values.push(start + step * i);
            }
            return values;
        };
        const pointFromCenter = (origin: THREE.Vector2, angle: number, targetRadius: number): THREE.Vector2 =>
            new THREE.Vector2(
                origin.x + Math.cos(angle) * targetRadius,
                origin.y + Math.sin(angle) * targetRadius
            );

        const outerAngles = sampleAngles(startAngle, endAngle, clockwise, arcSegments);
        const innerAngles = sampleAngles(endAngle, startAngle, !clockwise, arcSegments);
        const startCenter = pointFromCenter(center, startAngle, radius);
        const endCenter = pointFromCenter(center, endAngle, radius);
        const endCapAngles = sampleAngles(endAngle, endAngle + Math.PI, clockwise, capSegments);
        const startCapAngles = sampleAngles(startAngle + Math.PI, startAngle, clockwise, capSegments);

        const points: THREE.Vector2[] = [];
        outerAngles.forEach((angle) => {
            points.push(pointFromCenter(center, angle, outerRadius));
        });

        for (let i = 1; i < endCapAngles.length; i += 1) {
            points.push(pointFromCenter(endCenter, endCapAngles[i], halfWidth));
        }

        for (let i = 1; i < innerAngles.length; i += 1) {
            points.push(pointFromCenter(center, innerAngles[i], innerRadius));
        }

        for (let i = 1; i < startCapAngles.length; i += 1) {
            points.push(pointFromCenter(startCenter, startCapAngles[i], halfWidth));
        }

        if (points.length > 0) {
            points.push(points[0].clone());
        }
        return points;
    }

    /**
     * 由绘制点构建槽口轮廓与参数。
     * @param points 绘制点集合。
     * @returns 槽口轮廓、几何数据与参数；失败返回 `null`。
     */
    private buildSlotFromPoints(points: THREE.Vector3[]): {
        outlineWorld: THREE.Vector3[];
        geometryData: Record<string, any>;
        parameters: SlotParameters;
    } | null {
        const mode = this.slotOptions.mode;
        const lengthType = this.resolveSlotLengthType(mode, this.slotOptions.lengthType);
        const requiredPoints = this.getSlotPointCount(mode);
        if (points.length < requiredPoints) {
            return null;
        }
        const round = (value: number): number => Number(value.toFixed(8));
        // 获取基准面坐标系
        const coordSystem = {
            origin: this.referencePlane!.originWorld,
            normal: this.referencePlane!.normal.clone(),
            tangent: this.referencePlane!.xAxis,
            bitangent: this.referencePlane!.yAxis,
        };
        const normalizeDegrees = (degrees: number): number => {
            let value = degrees % 360;
            if (value < 0) {
                value += 360;
            }
            return value;
        };

        const localPoints = points.map((point) => this.referencePlane.worldToLocal(point));
        const widthSource = localPoints[requiredPoints - 1];
        const widthMin = SKETCH_GEOMETRY_TOLERANCE_CONFIG.slotMinimumWidth;

        if (mode === SketchSlotCommand.Straight || mode === SketchSlotCommand.CenterStraight) {
            const startCenter = mode === SketchSlotCommand.Straight
                ? localPoints[0].clone()
                : localPoints[0].clone().multiplyScalar(2).sub(localPoints[1]);
            const endCenter = mode === SketchSlotCommand.Straight
                ? localPoints[1].clone()
                : localPoints[1].clone();
            const axis = endCenter.clone().sub(startCenter);
            const centerLength = axis.length();
            if (!Number.isFinite(centerLength) || centerLength < SKETCH_GEOMETRY_TOLERANCE_CONFIG.slotMinimumRadius) {
                return null;
            }

            const axisDirection = axis.clone().normalize();
            const perpendicular = new THREE.Vector2(-axisDirection.y, axisDirection.x);
            const offset = widthSource.clone().sub(startCenter);
            const halfWidth = Math.abs(offset.dot(perpendicular));
            const width = Math.max(halfWidth * 2, widthMin);

            const outlineLocal = this.buildStraightSlotOutline2D(startCenter, endCenter, width / 2);
            if (outlineLocal.length < 4) {
                return null;
            }
            const outlineWorld = outlineLocal.map((point) => this.referencePlane.localToWorld(point));
            const centerLocal = startCenter.clone().add(endCenter).multiplyScalar(0.5);
            const centerWorld = this.referencePlane.localToWorld(centerLocal);
            const overallLength = centerLength + width;
            const displayLength = lengthType === 'overall' ? overallLength : centerLength;
            const angle = normalizeDegrees(THREE.MathUtils.radToDeg(Math.atan2(axis.y, axis.x)));

            return {
                outlineWorld,
                geometryData: {
                    mode,
                    lengthType,
                    addDimensions: this.slotOptions.addDimensions,
                    points: outlineWorld.map((point) => point.toArray()),
                    center: centerWorld.toArray(),
                    centerlineStart: this.referencePlane.localToWorld(startCenter).toArray(),
                    centerlineEnd: this.referencePlane.localToWorld(endCenter).toArray(),
                    centerLength,
                    overallLength,
                    displayLength,
                    width,
                    angle,
                    planeOrigin: coordSystem.origin.toArray(),
                    normal: [coordSystem.normal.x, coordSystem.normal.y, coordSystem.normal.z],
                    xAxis: [coordSystem.tangent.x, coordSystem.tangent.y, coordSystem.tangent.z],
                    yAxis: [coordSystem.bitangent.x, coordSystem.bitangent.y, coordSystem.bitangent.z],
                },
                parameters: {
                    mode,
                    center: centerWorld.toArray() as [number, number, number],
                    center2d: [round(centerLocal.x), round(centerLocal.y)],
                    start: this.referencePlane.localToWorld(startCenter).toArray() as [number, number, number],
                    start2d: [round(startCenter.x), round(startCenter.y)],
                    end: this.referencePlane.localToWorld(endCenter).toArray() as [number, number, number],
                    end2d: [round(endCenter.x), round(endCenter.y)],
                    width: round(width),
                    centerLength: round(centerLength),
                    overallLength: round(overallLength),
                    displayLength: round(displayLength),
                    angle: round(angle),
                },
            };
        }

        let centerLocal: THREE.Vector2;
        let radius = 0;
        let startAngle = 0;
        let endAngle = 0;
        let clockwise = false;
        let angleRange = 0;

        if (mode === SketchSlotCommand.ThreePointArc) {
            const arcData = buildArcFromThreePoints2D(
                new THREE.Vector2(localPoints[0].x, localPoints[0].y),
                new THREE.Vector2(localPoints[1].x, localPoints[1].y),
                new THREE.Vector2(localPoints[2].x, localPoints[2].y)
            );
            if (!arcData) {
                return null;
            }
            centerLocal = arcData.center.clone();
            radius = arcData.radius;
            startAngle = arcData.startAngle;
            endAngle = arcData.endAngle;
            clockwise = arcData.clockwise;
            angleRange = arcData.angleRange;
        } else {
            centerLocal = localPoints[0].clone();
            const radiusPoint = localPoints[1];
            const endHint = localPoints[2];
            radius = centerLocal.distanceTo(radiusPoint);
            if (!Number.isFinite(radius) || radius < SKETCH_GEOMETRY_TOLERANCE_CONFIG.slotMinimumRadius) {
                return null;
            }
            const rawStart = Math.atan2(radiusPoint.y - centerLocal.y, radiusPoint.x - centerLocal.x);
            const rawEnd = Math.atan2(endHint.y - centerLocal.y, endHint.x - centerLocal.x);
            // 中心点圆弧槽口需与第三点预览圆弧方向保持一致，避免落图后变成互补弧。
            clockwise = true;
            const normalized = normalizeArcAngles(rawStart, rawEnd, clockwise);
            startAngle = normalized.start;
            endAngle = normalized.end;
            angleRange = normalized.angleRange;
        }

        if (!Number.isFinite(radius) || radius < SKETCH_GEOMETRY_TOLERANCE_CONFIG.slotMinimumRadius) {
            return null;
        }

        const radialDistance = widthSource.distanceTo(centerLocal);
        const width = Math.max(Math.abs(radialDistance - radius) * 2, widthMin);
        const halfWidth = width / 2;
        const outlineLocal = this.buildArcSlotOutline2D(
            centerLocal,
            radius,
            startAngle,
            endAngle,
            clockwise,
            halfWidth
        );
        if (!outlineLocal || outlineLocal.length < 4) {
            return null;
        }

        const outlineWorld = outlineLocal.map((point) => this.referencePlane.localToWorld(point));
        const centerWorld = this.referencePlane.localToWorld(centerLocal);
        const startCenter = centerLocal.clone().add(new THREE.Vector2(Math.cos(startAngle), Math.sin(startAngle)).multiplyScalar(radius));
        const endCenter = centerLocal.clone().add(new THREE.Vector2(Math.cos(endAngle), Math.sin(endAngle)).multiplyScalar(radius));
        const startWorld = this.referencePlane.localToWorld(startCenter);
        const endWorld = this.referencePlane.localToWorld(endCenter);
        const centerLength = radius * angleRange;
        const overallLength = centerLength + width;
        const displayLength = lengthType === 'overall' ? overallLength : centerLength;

        return {
            outlineWorld,
            geometryData: {
                mode,
                lengthType,
                addDimensions: this.slotOptions.addDimensions,
                points: outlineWorld.map((point) => point.toArray()),
                center: centerWorld.toArray(),
                centerlineStart: startWorld.toArray(),
                centerlineEnd: endWorld.toArray(),
                centerLength,
                overallLength,
                displayLength,
                width,
                radius,
                angle: THREE.MathUtils.radToDeg(angleRange),
                startAngle,
                endAngle,
                clockwise,
                planeOrigin: coordSystem.origin.toArray(),
                normal: [coordSystem.normal.x, coordSystem.normal.y, coordSystem.normal.z],
                xAxis: [coordSystem.tangent.x, coordSystem.tangent.y, coordSystem.tangent.z],
                yAxis: [coordSystem.bitangent.x, coordSystem.bitangent.y, coordSystem.bitangent.z],
            },
            parameters: {
                mode,
                center: centerWorld.toArray() as [number, number, number],
                center2d: [round(centerLocal.x), round(centerLocal.y)],
                start: startWorld.toArray() as [number, number, number],
                start2d: [round(startCenter.x), round(startCenter.y)],
                end: endWorld.toArray() as [number, number, number],
                end2d: [round(endCenter.x), round(endCenter.y)],
                width: round(width),
                centerLength: round(centerLength),
                overallLength: round(overallLength),
                displayLength: round(displayLength),
                radius: round(radius),
                angle: round(THREE.MathUtils.radToDeg(angleRange)),
            },
        };
    }

    /** 判断槽口曲线输入预览是否仍可原位复用。
     * @returns 当前缓存对象是否仍由绘制管理器持有。 */
    private isSlotCurvePreviewActive(): boolean {
        return Boolean(this.slotCurvePreviewLine
            && this.sketchDrawingManager.previewCircle === this.slotCurvePreviewLine);
    }
    /**
     * 创建或原位更新槽口曲线输入预览。
     * 圆、圆心圆弧与三点圆弧共享固定采样拓扑；首次显示后只更新顶点与
     * BVH，不再重建材质或 GPU 顶点缓冲。
     * @param points 固定采样点列。
     */
    private syncSlotCurvePreviewLine(points: THREE.Vector3[]): void {
        if (this.isSlotCurvePreviewActive()) {
            this.updateSlotLineGeometryPoints(this.slotCurvePreviewLine as THREE.Line, points);
            return;
        }
        this.destroyPreview();
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        geometry.computeBoundingBox();
        geometry.computeBoundingSphere();
        const material = new THREE.LineBasicMaterial({
            color: SLOT_PREVIEW_COLOR, linewidth: 2,
            opacity: 0.8, transparent: true,
        });
        const line = new THREE.Line(geometry, material);
        line.name = 'slot-curve-input-preview';
        this.sketchDrawingManager.previewCircle = line;
        this.slotCurvePreviewLine = line;
        this.viewer.addObject(line);
    }

    /** 在处理器内创建“圆心+半径”圆预览。 */
    private createCirclePreviewByCenterInHandler(center: THREE.Vector3, edge: THREE.Vector3): void {
        const radius = center.distanceTo(edge);
        if (!Number.isFinite(radius) || radius <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            this.destroyPreview();
            return;
        }
        const localCenter = this.referencePlane.worldToLocal(center);
        const points = this.slotCurvePreviewPoints;
        for (let i = 0; i <= SLOT_CURVE_PREVIEW_SEGMENT_COUNT; i += 1) {
            const angle = (Math.PI * 2 * i) / SLOT_CURVE_PREVIEW_SEGMENT_COUNT;
            const x = localCenter.x + radius * Math.cos(angle);
            const y = localCenter.y + radius * Math.sin(angle);
            this.referencePlane.localToWorldTo(x, y, points[i]);
        }
        this.syncSlotCurvePreviewLine(points);
    }

    /** 在处理器内创建“圆心起止”圆弧预览。 */
    private createArcPreviewByCenterInHandler(center: THREE.Vector3, start: THREE.Vector3, end: THREE.Vector3): void {
        const localCenter = this.referencePlane.worldToLocal(center);
        const localStart = this.referencePlane.worldToLocal(start);
        const localEnd = this.referencePlane.worldToLocal(end);
        const radius = Math.hypot(localStart.x - localCenter.x, localStart.y - localCenter.y);
        if (!Number.isFinite(radius) || radius <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            this.destroyPreview();
            return;
        }
        let startAngle = Math.atan2(localStart.y - localCenter.y, localStart.x - localCenter.x);
        let endAngle = Math.atan2(localEnd.y - localCenter.y, localEnd.x - localCenter.x);
        if (endAngle > startAngle) {
            endAngle -= Math.PI * 2;
        }
        const points = this.slotCurvePreviewPoints;
        for (let i = 0; i <= SLOT_CURVE_PREVIEW_SEGMENT_COUNT; i += 1) {
            const t = i / SLOT_CURVE_PREVIEW_SEGMENT_COUNT;
            const angle = startAngle + (endAngle - startAngle) * t;
            const x = localCenter.x + radius * Math.cos(angle);
            const y = localCenter.y + radius * Math.sin(angle);
            this.referencePlane.localToWorldTo(x, y, points[i]);
        }
        this.syncSlotCurvePreviewLine(points);
    }
    /** 在处理器内创建“三点圆弧”预览。 */
    private createArcPreviewByThreePointsInHandler(start: THREE.Vector3, end: THREE.Vector3, through: THREE.Vector3): void {
        const localStart = this.referencePlane.worldToLocal(start);
        const localEnd = this.referencePlane.worldToLocal(end);
        const localThrough = this.referencePlane.worldToLocal(through);
        const arcData = buildArcFromThreePoints2D(localStart, localEnd, localThrough);
        if (!arcData) {
            this.destroyPreview();
            return;
        }
        const points = this.slotCurvePreviewPoints;
        for (let i = 0; i <= SLOT_CURVE_PREVIEW_SEGMENT_COUNT; i += 1) {
            const t = i / SLOT_CURVE_PREVIEW_SEGMENT_COUNT;
            const angle = arcData.startAngle + (arcData.endAngle - arcData.startAngle) * t;
            const x = arcData.center.x + arcData.radius * Math.cos(angle);
            const y = arcData.center.y + arcData.radius * Math.sin(angle);
            this.referencePlane.localToWorldTo(x, y, points[i]);
        }
        this.syncSlotCurvePreviewLine(points);
    }

    /**
     * 解析槽口绘制点。
     */
    private resolveSlotDrawingPoint(payload: SketchDrawingMouseDownPayload): THREE.Vector3 | null {
        const planePoint = payload.snappedPoint ?? this.getDrawingPoint(payload.event);
        if (!planePoint) {
            return null;
        }
        return planePoint
    }

    public parseVector3Tuple(value: unknown): [number, number, number] | null {
        if (!Array.isArray(value) || value.length < 3) {
            return null;
        }
        const x = Number(value[0]);
        const y = Number(value[1]);
        const z = Number(value[2]);
        if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
            return null;
        }
        return [x, y, z];
    }

    // ─── 槽口对象组装 ────────────────────────────────────────────────────────

    /**
     * 销毁预览用的 SketchSlotObject。
     */
    private destroySlotPreview(): void {
        if (!this.previewSlotObject) {
            return;
        }
        if (this.previewSlotObject.parent) {
            this.previewSlotObject.parent.remove(this.previewSlotObject);
        }
        // 逐级销毁子边几何与材质。
        this.previewSlotObject.getEdges().forEach((edge) => {
            edge.geometry?.dispose();
            const material = edge.material;
            if (Array.isArray(material)) {
                material.forEach((m) => m.dispose());
            } else if (material) {
                material.dispose();
            }
        });
        this.previewSlotObject = null;
    }

    /**
     * 从绘制点计算槽口 4 条边的分解数据。
     * @param points 绘制点集合。
     * @returns 分解数据；失败返回 null。
     */
    private computeSlotEdgeDecomposition(points: THREE.Vector3[]): SlotEdgeDecomposition | null {
        const mode = this.slotOptions.mode;
        const requiredPoints = this.getSlotPointCount(mode);
        if (points.length < requiredPoints) {
            return null;
        }

        const localPoints = points.map((p) => this.referencePlane.worldToLocal(p));
        const widthSource = localPoints[requiredPoints - 1];
        const widthMin = SKETCH_GEOMETRY_TOLERANCE_CONFIG.slotMinimumWidth;

        if (mode === SketchSlotCommand.Straight || mode === SketchSlotCommand.CenterStraight) {
            return this.computeStraightSlotDecomposition(localPoints, widthSource, widthMin, mode);
        }
        return this.computeArcSlotDecomposition(localPoints, widthSource, widthMin, mode);
    }

    /**
     * 计算直槽口的边缘分解数据。
     * @param localPoints 参考面局部坐标点集。
     * @param widthSource 宽度参考点。
     * @param widthMin 最小宽度。
     * @param mode 槽口模式。
     * @returns 直槽口边缘分解数据。
     */
    private computeStraightSlotDecomposition(
        localPoints: THREE.Vector2[],
        widthSource: THREE.Vector2,
        widthMin: number,
        mode: SketchSlotCommand,
    ): SlotEdgeDecomposition | null {
        const startCenter = mode === SketchSlotCommand.Straight
            ? localPoints[0].clone()
            : localPoints[0].clone().multiplyScalar(2).sub(localPoints[1]);
        const endCenter = mode === SketchSlotCommand.Straight
            ? localPoints[1].clone()
            : localPoints[1].clone();
        const axis = endCenter.clone().sub(startCenter);
        const centerLength = axis.length();
        if (!Number.isFinite(centerLength) || centerLength < SKETCH_GEOMETRY_TOLERANCE_CONFIG.slotMinimumRadius) {
            return null;
        }

        const axisDirection = axis.clone().normalize();
        const perpendicular = new THREE.Vector2(-axisDirection.y, axisDirection.x);
        const offset = widthSource.clone().sub(startCenter);
        const halfWidth = Math.max(Math.abs(offset.dot(perpendicular)), widthMin / 2);

        // 4 个接合点（局部 2D）。
        const p1Local = startCenter.clone().add(perpendicular.clone().multiplyScalar(halfWidth));
        const p2Local = endCenter.clone().add(perpendicular.clone().multiplyScalar(halfWidth));
        const p3Local = endCenter.clone().add(perpendicular.clone().multiplyScalar(-halfWidth));
        const p4Local = startCenter.clone().add(perpendicular.clone().multiplyScalar(-halfWidth));

        // 转世界坐标。
        const p1World = this.referencePlane.localToWorld(p1Local);
        const p2World = this.referencePlane.localToWorld(p2Local);
        const p3World = this.referencePlane.localToWorld(p3Local);
        const p4World = this.referencePlane.localToWorld(p4Local);

        // 端帽弧定义：起始帽（P4 → P1，以 startCenter 为圆心）。
        const theta = Math.atan2(axisDirection.y, axisDirection.x);
        const startCapStartAngle = theta - Math.PI / 2;
        const startCapEndAngle = theta + Math.PI / 2;
        const endCapStartAngle = theta + Math.PI / 2;
        const endCapEndAngle = theta - Math.PI / 2;

        const startCapArc: SlotArcEdgeDef = {
            centerWorld: this.referencePlane.localToWorld(startCenter),
            radius: halfWidth,
            startAngle: startCapStartAngle,
            endAngle: startCapEndAngle,
            clockwise: true,
        };
        const endCapArc: SlotArcEdgeDef = {
            centerWorld: this.referencePlane.localToWorld(endCenter),
            radius: halfWidth,
            startAngle: endCapStartAngle,
            endAngle: endCapEndAngle,
            clockwise: true,
        };

        return {
            type: 'straight',
            p1World,
            p2World,
            p3World,
            p4World,
            endCapArc,
            startCapArc,
        };
    }

    /**
     * 计算圆弧槽口的边缘分解数据。
     * @param localPoints 参考面局部坐标点集。
     * @param widthSource 宽度参考点。
     * @param widthMin 最小宽度。
     * @param mode 槽口模式。
     * @returns 圆弧槽口边缘分解数据。
     */
    private computeArcSlotDecomposition(
        localPoints: THREE.Vector2[],
        widthSource: THREE.Vector2,
        widthMin: number,
        mode: SketchSlotCommand,
    ): SlotEdgeDecomposition | null {
        let centerLocal: THREE.Vector2;
        let radius = 0;
        let startAngle = 0;
        let endAngle = 0;
        let clockwise = false;

        if (mode === SketchSlotCommand.ThreePointArc) {
            const arcData = buildArcFromThreePoints2D(
                new THREE.Vector2(localPoints[0].x, localPoints[0].y),
                new THREE.Vector2(localPoints[1].x, localPoints[1].y),
                new THREE.Vector2(localPoints[2].x, localPoints[2].y)
            );
            if (!arcData) {
                return null;
            }
            centerLocal = arcData.center.clone();
            radius = arcData.radius;
            startAngle = arcData.startAngle;
            endAngle = arcData.endAngle;
            clockwise = arcData.clockwise;
        } else {
            centerLocal = localPoints[0].clone();
            const radiusPoint = localPoints[1];
            const endHint = localPoints[2];
            radius = centerLocal.distanceTo(radiusPoint);
            if (!Number.isFinite(radius) || radius < SKETCH_GEOMETRY_TOLERANCE_CONFIG.slotMinimumRadius) {
                return null;
            }
            const rawStart = Math.atan2(radiusPoint.y - centerLocal.y, radiusPoint.x - centerLocal.x);
            const rawEnd = Math.atan2(endHint.y - centerLocal.y, endHint.x - centerLocal.x);
            clockwise = true;
            const normalized = normalizeArcAngles(rawStart, rawEnd, clockwise);
            startAngle = normalized.start;
            endAngle = normalized.end;
        }

        if (!Number.isFinite(radius) || radius < SKETCH_GEOMETRY_TOLERANCE_CONFIG.slotMinimumRadius) {
            return null;
        }

        const radialDistance = widthSource.distanceTo(centerLocal);
        const halfWidth = Math.max(Math.abs(radialDistance - radius), widthMin / 2);
        const outerRadius = radius + halfWidth;
        const innerRadius = Math.max(radius - halfWidth, SKETCH_GEOMETRY_TOLERANCE_CONFIG.slotMinimumRadius);

        // 4 个接合点（局部 2D → 世界坐标）。
        const p1Local = centerLocal.clone().add(
            new THREE.Vector2(Math.cos(startAngle), Math.sin(startAngle)).multiplyScalar(outerRadius)
        );
        const p2Local = centerLocal.clone().add(
            new THREE.Vector2(Math.cos(endAngle), Math.sin(endAngle)).multiplyScalar(outerRadius)
        );
        const p3Local = centerLocal.clone().add(
            new THREE.Vector2(Math.cos(endAngle), Math.sin(endAngle)).multiplyScalar(innerRadius)
        );
        const p4Local = centerLocal.clone().add(
            new THREE.Vector2(Math.cos(startAngle), Math.sin(startAngle)).multiplyScalar(innerRadius)
        );

        const p1World = this.referencePlane.localToWorld(p1Local);
        const p2World = this.referencePlane.localToWorld(p2Local);
        const p3World = this.referencePlane.localToWorld(p3Local);
        const p4World = this.referencePlane.localToWorld(p4Local);

        const arcCenter = this.referencePlane.localToWorld(centerLocal);
        const startCenter = centerLocal.clone().add(
            new THREE.Vector2(Math.cos(startAngle), Math.sin(startAngle)).multiplyScalar(radius)
        );
        const endCenter = centerLocal.clone().add(
            new THREE.Vector2(Math.cos(endAngle), Math.sin(endAngle)).multiplyScalar(radius)
        );

        // 外弧：P1 → P2（与中心线弧同方向）。
        const outerArc: SlotArcEdgeDef = {
            centerWorld: arcCenter.clone(),
            radius: outerRadius,
            startAngle,
            endAngle,
            clockwise,
        };
        // 内弧：P3 → P4（与中心线弧反方向）。
        const innerArc: SlotArcEdgeDef = {
            centerWorld: arcCenter.clone(),
            radius: innerRadius,
            startAngle: endAngle,
            endAngle: startAngle,
            clockwise: !clockwise,
        };
        // 终止端帽：P2 → P3（以 endCenter 为圆心）。
        const endCapArc: SlotArcEdgeDef = {
            centerWorld: this.referencePlane.localToWorld(endCenter),
            radius: halfWidth,
            startAngle: endAngle,
            endAngle: endAngle + Math.PI,
            clockwise,
        };
        // 起始端帽：P4 → P1（以 startCenter 为圆心）。
        const startCapArc: SlotArcEdgeDef = {
            centerWorld: this.referencePlane.localToWorld(startCenter),
            radius: halfWidth,
            startAngle: startAngle + Math.PI,
            endAngle: startAngle,
            clockwise,
        };

        return {
            type: 'arc',
            p1World,
            p2World,
            p3World,
            p4World,
            outerArc,
            innerArc,
            endCapArc,
            startCapArc,
        };
    }

    /**
     * 根据边缘分解数据组装 SketchSlotObject。
     * @param decomposition 分解数据。
     * @param isPreview 是否为预览模式（预览使用预览材质）。
     * @returns 组装后的槽口对象；参数无效返回 null。
     */
    private assembleSketchSlotObject(
        decomposition: SlotEdgeDecomposition,
        isPreview: boolean,
    ): SketchSlotObject | null {
        const mode = this.slotOptions.mode;
        const isConstruction = this.slotOptions.forConstruction;
        const referencePlane = this.referencePlane;

        // 创建材质。
        const material = isPreview
            ? (isConstruction ? LineMaterialFactory.getPreviewDashedMaterial() : LineMaterialFactory.getPreviewBasicMaterial())
            : (isConstruction ? LineMaterialFactory.createConstructionLineMaterial() : undefined);
        const controlPointMaterial = PointsMaterialFactory.getSketchControlPointSpriteMaterial();

        /**
         * 预览态下统一给控制点打上绘制预览标记，避免进入点捕捉链路。
         * @param point 待标记的控制点。
         */
        const applyPreviewPointFlag = (point: SketchPointObject): void => {
            if (isPreview) {
                point.isDrawingPreviewPoint = true;
            }
        };


        // 创建 4 个共享接合点。
        const p1 = new SketchPointObject({
            position: decomposition.p1World,
            pointKind: SketchPointKind.ControlPoint,
            material: controlPointMaterial,
            referencePlane,
        });

        applyPreviewPointFlag(p1);

        const p2 = new SketchPointObject({
            position: decomposition.p2World,
            pointKind: SketchPointKind.ControlPoint,
            material: controlPointMaterial,
            referencePlane,
        });

        applyPreviewPointFlag(p2);

        const p3 = new SketchPointObject({
            position: decomposition.p3World,
            pointKind: SketchPointKind.ControlPoint,
            material: controlPointMaterial,
            referencePlane,
        });

        applyPreviewPointFlag(p3);

        const p4 = new SketchPointObject({
            position: decomposition.p4World,
            pointKind: SketchPointKind.ControlPoint,
            material: controlPointMaterial,
            referencePlane,
        });

        applyPreviewPointFlag(p4);


        /**
         * 创建槽口控制点。
         *
         * 说明：控制点角色由 `SketchSlotObject` 构造时按结构统一登记，绘制侧不重复声明，
         * 避免"只有绘制路径有角色、反序列化/撤销重建没有"的历史缺口。
         *
         * @param position 世界坐标。
         * @returns 控制点。
         */
        const createSlotControlPoint = (position: THREE.Vector3): SketchPointObject => {
            const point = new SketchPointObject({
                position: position.clone(),
                pointKind: SketchPointKind.ControlPoint,
                material: controlPointMaterial,
                referencePlane,
            });

            applyPreviewPointFlag(point);

            return point;
        };

        /**
         * 端帽弧圆心 = 槽口中心线端点（直槽口为中心线起止点、圆弧槽口为中心线弧起止点）。
         *
         * 必须**同一个点对象**：此前端帽弧各自新建圆心点，与中心线端点位置重合却是两个对象，
         * 拾取时可能命中没有槽口角色的那一个 → 走通用点拖拽 → 只挪圆心、槽口散架。
         */
        const startCapCenterPoint = createSlotControlPoint(decomposition.startCapArc.centerWorld);
        const endCapCenterPoint = createSlotControlPoint(decomposition.endCapArc.centerWorld);

        // 创建辅助函数：构建圆弧边。
        const buildArc = (
            def: SlotArcEdgeDef,
            startPoint: SketchPointObject,
            endPoint: SketchPointObject,
            sharedCenterPoint?: SketchPointObject,
        ): SketchArcObject => {
            // 圆心控制点（外部传入时复用，保证槽口内同一位置只有一个可拖拽点）。
            const centerPoint = sharedCenterPoint ?? new SketchPointObject({
                position: def.centerWorld.clone(),
                pointKind: SketchPointKind.ControlPoint,
                material: controlPointMaterial,
                referencePlane,
            });

            applyPreviewPointFlag(centerPoint);

            const arcDef: SketchArcDefinition = {
                centerWorld: def.centerWorld.clone(),
                radius: def.radius,
                startAngle: def.startAngle,
                endAngle: def.endAngle,
                clockwise: def.clockwise,
            };
            const arc = new SketchArcObject({
                startPoint,
                endPoint,
                centerPoint,
                definition: arcDef,
                referencePlane,
                material,

                // 槽口预览圆弧只用于视觉反馈，不允许参与点捕捉。
                isDrawingPreviewArc: isPreview,

                isConstruction,
            });
            // 构造函数只创建两点直线几何，需手动触发弧线几何构建。
            arc.updateArc();
            return arc;
        };

        // 终止端帽弧：P2 → P3（圆心复用中心线终止端控制点）。
        const endCapArc = buildArc(decomposition.endCapArc, p2, p3, endCapCenterPoint);
        // 起始端帽弧：P4 → P1（圆心复用中心线起始端控制点）。
        const startCapArc = buildArc(decomposition.startCapArc, p4, p1, startCapCenterPoint);
        const junctionPoints = {p1, p2, p3, p4};

        // 根据分解类型分别构建。
        if (decomposition.type === 'straight') {
            // 直槽口：上下为直线，左右为圆弧端帽。
            const topLine = new SketchLineObject({
                startPoint: p1,
                endPoint: p2,
                referencePlane,
                material,

                // 槽口预览直线只用于视觉反馈，不允许参与点捕捉。
                isDrawingPreviewLine: isPreview,

                isConstruction,
            });
            const bottomLine = new SketchLineObject({
                startPoint: p3,
                endPoint: p4,
                referencePlane,
                material,

                // 槽口预览直线只用于视觉反馈，不允许参与点捕捉。
                isDrawingPreviewLine: isPreview,

                isConstruction,
            });


            /**
             * 中心线两端点 **就是**端帽弧圆心（同一个点对象，见上方共享圆心说明），
             * 中心点 = 中心线中点。
             */
            const centerlineStartPoint = startCapCenterPoint;
            const centerlineEndPoint = endCapCenterPoint;
            const centerWorld = decomposition.startCapArc.centerWorld.clone()
                .add(decomposition.endCapArc.centerWorld).multiplyScalar(0.5);
            const centerPoint = createSlotControlPoint(centerWorld);

            // 创建构造中心线（虚线样式）。
            const constructionCenterline = new SketchLineObject({
                startPoint: centerlineStartPoint,
                endPoint: centerlineEndPoint,
                referencePlane,
                material: LineMaterialFactory.createConstructionLineMaterial(),

                // 槽口预览构造中心线也不允许参与点捕捉。
                isDrawingPreviewLine: isPreview,

                isConstruction: true,
            });

            //------ 构造中心线距离同步收口 开始------
            /**
             * 构造线 Node 材质依赖 lineDistance/lineDistancePercent/points 节奏输入，
             * 装配路径注入材质时不会自动同步，必须在此显式刷新一次几何，
             * 否则中心线会以“无节奏输入”状态渲染成实线（上色草图轮廓开关曾间接影响该链路）。
             */
            constructionCenterline.updateGeometry();
            //------ 构造中心线距离同步收口 结束------


            const slotObject = new SketchSlotObject({
                mode,
                referencePlane,
                topLine,
                bottomLine,
                startCapArc,
                endCapArc,
                junctionPoints,
                centerlineStartPoint,
                centerlineEndPoint,
                centerPoint,
                constructionCenterline,
            });


            // 将 topLine / bottomLine / constructionCenterline 绑定到 SketchSlotObject，
            // 使它们被识别为封闭几何的一部分（拖拽留影、轮廓联动等）。
            /**
             * 将直槽口的两条直边和构造中心线都绑定到封闭集合，
             * 这样选择、轮廓联动和拖拽行为才能保持一致。
             */
            slotObject.getEdges().forEach((edge) => {
                edge.attachClosedCollection(slotObject);
            });
            constructionCenterline.attachClosedCollection(slotObject);


            return slotObject;
        }


        /**
         * 圆弧槽口的弧线圆心：内外弧共用同一个点对象。
         * 三个同位置对象（外弧圆心/内弧圆心/槽口圆心控制点）中有两个没有槽口角色，
         * 拾取到它们就会走通用点拖拽把槽口拖散。
         */
        const arcCenterPoint = createSlotControlPoint(decomposition.outerArc.centerWorld);


        // 圆弧槽口：4 条边均为圆弧（内外弧共享槽口圆心控制点）。
        const outerArc = buildArc(decomposition.outerArc, p1, p2, arcCenterPoint);
        const innerArc = buildArc(decomposition.innerArc, p3, p4, arcCenterPoint);


        // 计算中心线弧的角度中点位置。
        const outerDef = decomposition.outerArc;
        const innerDef = decomposition.innerArc;
        // 中心线半径 = 外弧半径与内弧半径的均值。
        const centerlineRadius = (outerDef.radius + innerDef.radius) / 2;
        // 计算角度中点（需考虑弧线方向，避免跨越 2π 时取错侧）。
        let midAngle: number;
        if (outerDef.clockwise) {
            let sweep = outerDef.startAngle - outerDef.endAngle;
            if (sweep < 0) sweep += 2 * Math.PI;
            midAngle = outerDef.startAngle - sweep / 2;
        } else {
            let sweep = outerDef.endAngle - outerDef.startAngle;
            if (sweep < 0) sweep += 2 * Math.PI;
            midAngle = outerDef.startAngle + sweep / 2;
        }
        // 中心线弧中点的局部 2D 坐标 → 世界坐标。
        const arcLocalCenter = this.referencePlane!.worldToLocal(outerDef.centerWorld.clone());
        const midLocal = new THREE.Vector2(
            arcLocalCenter.x + centerlineRadius * Math.cos(midAngle),
            arcLocalCenter.y + centerlineRadius * Math.sin(midAngle),
        );
        const centerArcMidpointPos = this.referencePlane!.localToWorld(midLocal);
        const centerArcMidpoint = new SketchPointObject({
            position: centerArcMidpointPos,
            pointKind: SketchPointKind.ControlPoint,
            material: controlPointMaterial,
            referencePlane,
        });

        applyPreviewPointFlag(centerArcMidpoint);


        /**
         * 创建构造中心线弧（与直槽口构造中心线对齐：实体级构造线，虚线样式）。
         *
         * 设计说明：
         * 1. 起点/终点复用端帽弧圆心共享点，圆心复用槽口圆心控制点——
         *    与直槽口“端帽圆心 = 中心线端点”共享拓扑保持一致；
         * 2. 弧线角度与外弧同源（槽口边沿射线方向定义，外弧角度即中心线弧角度）；
         * 3. `SketchArcObject` 构造器已同步几何与线距离，构造后即具备点划线节奏。
         */
        const constructionCenterlineArc = new SketchArcObject({
            startPoint: startCapCenterPoint,
            endPoint: endCapCenterPoint,
            centerPoint: arcCenterPoint,
            definition: {
                centerWorld: outerDef.centerWorld.clone(),
                radius: centerlineRadius,
                startAngle: outerDef.startAngle,
                endAngle: outerDef.endAngle,
                clockwise: outerDef.clockwise,
            },
            referencePlane,
            material: LineMaterialFactory.createConstructionLineMaterial(),

            // 槽口预览构造中心线弧也不允许参与点捕捉。
            isDrawingPreviewArc: isPreview,

            isConstruction: true,
        });

        const slotObject = new SketchSlotObject({
            mode,
            referencePlane,
            outerArc,
            innerArc,
            startCapArc,
            endCapArc,
            junctionPoints,
            arcCenterPoint,
            centerArcMidpoint,
            constructionCenterlineArc,
        });
        slotObject.getEdges().forEach((edge) => {
            edge.attachClosedCollection(slotObject);
        });
        constructionCenterlineArc.attachClosedCollection(slotObject);
        return slotObject;
    }
}
